import { serializeTenantQueryArgs } from '@core/api/tenantContext';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';

/**
 * Auth API using RTK Query
 */
export const authApi = createApi({
  serializeQueryArgs: serializeTenantQueryArgs,
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['Team', 'Invitation', 'Organizations'],
  endpoints: (builder) => ({
    getSession: builder.query({
      query: () => '/api/auth/session/',
    }),
    logout: builder.mutation({
      query: () => ({ url: '/api/auth/logout/', method: 'POST' }),
    }),
    login: builder.mutation({
      query: (credentials) => ({
        url: '/api/auth/login/',
        method: 'POST',
        body: credentials,
      }),
    }),
    requestMagicLink: builder.mutation({
      query: (email) => ({
        url: '/api/auth/magic-link/request/',
        method: 'POST',
        body: { email },
      }),
    }),
    verifyMagicLink: builder.mutation({
      query: (token) => ({
        url: '/api/auth/magic-link/verify/',
        method: 'POST',
        body: { token },
      }),
    }),
    acceptInvitation: builder.mutation({
      query: (token) => ({
        url: '/api/accounts/invitations/accept/',
        method: 'POST',
        body: { token },
      }),
    }),
    // Both tenant-changing mutations invalidate Team/Invitation: the
    // setCredentials store listener resets the other tenant-bound APIs but
    // not authApi, and getInvitations takes no argument, so a stale cache
    // would show the previous tenant's members and pending invitations.
    switchOrganization: builder.mutation({
      query: (organizationUuid) => ({
        url: '/api/auth/switch-organization/',
        method: 'POST',
        body: { organization_uuid: organizationUuid },
      }),
      invalidatesTags: ['Team', 'Invitation'],
    }),
    createOrganization: builder.mutation({
      query: (name) => ({
        url: '/api/accounts/organizations/',
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['Team', 'Invitation', 'Organizations'],
    }),
    // Superuser only: every tenant on the platform, for the impersonation picker.
    listAllOrganizations: builder.query({
      query: () => '/api/accounts/organizations/',
      providesTags: ['Organizations'],
    }),
    impersonateOrganization: builder.mutation({
      query: (organizationUuid) => ({
        url: '/api/auth/impersonate/',
        method: 'POST',
        body: { organization_uuid: organizationUuid },
      }),
      invalidatesTags: ['Team', 'Invitation'],
    }),
    stopImpersonation: builder.mutation({
      query: () => ({ url: '/api/auth/impersonate/', method: 'DELETE' }),
      invalidatesTags: ['Team', 'Invitation'],
    }),
    getOrganizationMembers: builder.query({
      query: (organizationUuid) => `/api/accounts/organizations/${organizationUuid}/members/`,
      providesTags: ['Team'],
    }),
    getInvitations: builder.query({
      query: () => '/api/accounts/invitations/',
      providesTags: ['Invitation'],
    }),
    inviteMember: builder.mutation({
      query: (body) => ({ url: '/api/accounts/invitations/', method: 'POST', body }),
      invalidatesTags: ['Invitation'],
    }),
    updateMe: builder.mutation({
      query: (body) => ({ url: '/api/accounts/users/me_update/', method: 'PUT', body }),
    }),
    forgotPassword: builder.mutation({
      query: (email) => ({
        url: '/api/auth/forgot-password/',
        method: 'POST',
        body: { email },
      }),
    }),
    resetPassword: builder.mutation({
      query: (data) => ({
        url: '/api/auth/reset-password/',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetSessionQuery,
  useLogoutMutation,
  useLoginMutation,
  useRequestMagicLinkMutation,
  useVerifyMagicLinkMutation,
  useAcceptInvitationMutation,
  useSwitchOrganizationMutation,
  useCreateOrganizationMutation,
  useListAllOrganizationsQuery,
  useImpersonateOrganizationMutation,
  useStopImpersonationMutation,
  useGetOrganizationMembersQuery,
  useGetInvitationsQuery,
  useInviteMemberMutation,
  useUpdateMeMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = authApi;
